import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, ErrorState } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../api/errorMapping';
import { Link } from '@tanstack/react-router';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type SortingState,
} from '@tanstack/react-table';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { Gate } from '../permissions/Gate';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ContentLoading } from '../shared/ContentLoading';
import { DataTableToolbar } from '../shared/DataTableToolbar';
import { DataTablePagination } from '../shared/DataTablePagination';
import { buildRoleColumns, ROLE_TABLE_HIDDEN_COLUMNS } from '../roles/roleTableColumns';
import { useLabelResolver } from '../../hooks/useLabelResolver';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { globalFilterFn } from '../../lib/dataTable';
import { RoleForm } from './RoleForm';
import { RolePermissionsView } from './RolePermissionsView';
import type { TenantRoleListItemLocal } from '../../api/types/TenantLocal';

export function RolesTable() {
    const { user } = useAuth();
    const tid = user?.tenantId ?? '';
    const [createOpen, setCreateOpen] = useState(false);
    // Rol cuyos permisos se están viendo (modal read-only). null = cerrado.
    const [viewing, setViewing] = useState<TenantRoleListItemLocal | null>(null);
    // Rol de sistema que se está clonando a personalizado. null = cerrado.
    const [cloning, setCloning] = useState<TenantRoleListItemLocal | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'roleName', desc: false }]);
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['tenant', tid, 'roles'],
        queryFn: () => tenantApi.listRoles(tid),
        enabled: !!tid,
    });

    const labels = useLabelResolver();

    const columns = useMemo(
        () =>
            buildRoleColumns({
                labels,
                renderActions: (role, isSystem) => (
                    <div className="flex items-center justify-center gap-3">
                        <button
                            type="button"
                            className="text-brand-accent hover:underline"
                            onClick={() => {
                                setViewing(role);
                            }}
                        >
                            Ver permisos
                        </button>
                        {isSystem ? (
                            <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tid}>
                                <button
                                    type="button"
                                    className="text-brand-accent hover:underline"
                                    onClick={() => {
                                        setCloning(role);
                                    }}
                                >
                                    Clonar a personalizado
                                </button>
                            </Gate>
                        ) : (
                            <Link
                                to="/tenant/roles/$roleId"
                                params={{ roleId: role.roleId }}
                                className="text-brand-accent hover:underline"
                            >
                                Editar
                            </Link>
                        )}
                    </div>
                ),
            }),
        [labels, tid],
    );

    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 }, columnVisibility: ROLE_TABLE_HIDDEN_COLUMNS },
    });

    if (isLoading) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar los roles"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tid}>
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear rol personalizado
                    </Button>
                </Gate>
            </div>

            {/* Modal plano: crear rol personalizado sin navegar de página. */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="inline-flex items-center gap-1.5 text-lg font-semibold">
                                Crear rol personalizado
                                <HelpHint text={HELP_TEXT.customRole} />
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <RoleForm
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Modal de solo lectura con los permisos de un rol. */}
            {viewing && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setViewing(null);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                Permisos de {viewing.roleName}
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setViewing(null);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <RolePermissionsView permissions={viewing.permissions ?? []} />
                    </div>
                </div>
            )}

            {/* Modal: clonar un rol de sistema a uno personalizado, con sus permisos precargados. */}
            {cloning && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCloning(null);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                Clonar {cloning.roleName} a personalizado
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCloning(null);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <RoleForm
                            initialName={`Copia de ${cloning.roleName}`}
                            initialPermissions={cloning.permissions ?? []}
                            onDone={() => {
                                setCloning(null);
                            }}
                        />
                    </div>
                </div>
            )}
            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar por nombre…"
            />

            {rows.length === 0 ? (
                <EmptyState
                    title="Esta organización todavía no tiene roles"
                    description="Cuando crees un rol, aparecerá aquí."
                />
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map((h) => (
                                        <TableHead key={h.id} className="text-center">
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((r) => (
                                <TableRow key={r.id}>
                                    {r.getVisibleCells().map((c) => (
                                        <TableCell key={c.id} className="text-center align-middle">
                                            {flexRender(c.column.columnDef.cell, c.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <DataTablePagination table={table} />
                </>
            )}
        </div>
    );
}
